import { useEffect, useRef } from 'react';

import { detectWedgeScan, type KeyStroke } from './scan-result';

/** A pause longer than this starts a new burst. */
const BURST_RESET_MS = 120;

/**
 * Listens for USB/Bluetooth handscanners anywhere in the app. They behave like
 * a keyboard that types very fast and presses Enter, so a fast digit burst
 * ending in Enter counts as a scan. Typing into form fields is left alone,
 * except for fields that opt in with data-wedge-target.
 */
export function useWedgeScanner(onScan: (code: string) => void, enabled = true) {
  const strokes = useRef<KeyStroke[]>([]);
  const onScanRef = useRef(onScan);

  useEffect(() => {
    onScanRef.current = onScan;
  }, [onScan]);

  useEffect(() => {
    if (!enabled) return undefined;

    function isEditable(target: EventTarget | null): boolean {
      if (!(target instanceof HTMLElement)) return false;
      if (target.closest('[data-wedge-target]')) return false;
      return (
        target.isContentEditable ||
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement
      );
    }

    function onKeyDown(event: KeyboardEvent) {
      if (event.ctrlKey || event.altKey || event.metaKey) return;
      if (isEditable(event.target)) return;

      const now = performance.now();
      const last = strokes.current[strokes.current.length - 1];
      if (last && now - last.at > BURST_RESET_MS) strokes.current = [];

      if (event.key === 'Enter') {
        const code = detectWedgeScan(strokes.current);
        strokes.current = [];
        if (code) {
          event.preventDefault();
          onScanRef.current(code);
        }
        return;
      }

      if (/^\d$/.test(event.key)) {
        strokes.current.push({ key: event.key, at: now });
      } else {
        strokes.current = [];
      }
    }

    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [enabled]);
}
